import {
  ApartmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudOutlined,
  ColumnHeightOutlined,
  HomeOutlined,
  RiseOutlined,
  RocketOutlined,
  UserOutlined,
  VerticalAlignTopOutlined,
} from '@ant-design/icons';
import { MeterAccessibility } from '@enmon/commons/lib/enmon';
import { Button, Flex, Typography } from 'antd';
import type { ReactNode } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { accessibilityMessage } from '@/features/inventorization/messages';

export const accessibilityOptions: Array<{ icon: ReactNode; label: string; value: MeterAccessibility }> = [
  { icon: <CheckCircleOutlined />, label: 'Good access', value: MeterAccessibility.Good },
  { icon: <CloseCircleOutlined />, label: 'Bad access', value: MeterAccessibility.Bad },
  { icon: <UserOutlined />, label: 'Tenant access', value: MeterAccessibility.Tenant },
  { icon: <HomeOutlined />, label: 'Basement', value: MeterAccessibility.Basement },
  { icon: <VerticalAlignTopOutlined />, label: 'Ceiling', value: MeterAccessibility.Ceiling },
  { icon: <ApartmentOutlined />, label: 'Shaft', value: MeterAccessibility.Shaft },
  { icon: <CloudOutlined />, label: 'Flooded shaft', value: MeterAccessibility.FloodedShaft },
  { icon: <ColumnHeightOutlined />, label: 'Over 2 m / ladder', value: MeterAccessibility.High },
  { icon: <RiseOutlined />, label: 'Over 3 m', value: MeterAccessibility.FairlyHigh },
  { icon: <RocketOutlined />, label: 'Over 5 m', value: MeterAccessibility.VeryHigh },
];

const accessibilityKeyByValue: Record<MeterAccessibility, string> = {
  [MeterAccessibility.Bad]: 'bad',
  [MeterAccessibility.Basement]: 'basement',
  [MeterAccessibility.Ceiling]: 'ceiling',
  [MeterAccessibility.FairlyHigh]: 'fairlyHigh',
  [MeterAccessibility.FloodedShaft]: 'floodedShaft',
  [MeterAccessibility.Good]: 'good',
  [MeterAccessibility.High]: 'high',
  [MeterAccessibility.Shaft]: 'shaft',
  [MeterAccessibility.Tenant]: 'tenant',
  [MeterAccessibility.VeryHigh]: 'veryHigh',
};

export function AccessibilityPicker({
  onChange,
  value,
}: {
  onChange: (value: MeterAccessibility[]) => void;
  value: MeterAccessibility[];
}) {
  const intl = useIntl();

  function toggleValue(nextValue: MeterAccessibility) {
    onChange(value.includes(nextValue) ? value.filter(candidate => candidate !== nextValue) : [...value, nextValue]);
  }

  return (
    <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
      {accessibilityOptions.map(option => {
        const isSelected = value.includes(option.value);

        return (
          <Button
            aria-pressed={isSelected}
            key={option.value}
            onClick={() => toggleValue(option.value)}
            size="large"
            style={{ height: 64, paddingInline: 8, whiteSpace: 'normal' }}
            type={isSelected ? 'primary' : 'default'}
          >
            <Flex align="center" gap="small" justify="center">
              <Typography.Text style={{ color: 'inherit', fontSize: 20 }}>{option.icon}</Typography.Text>
              <span>{formatAccessibilityLabel(intl, option.value)}</span>
            </Flex>
          </Button>
        );
      })}
    </div>
  );
}

export function formatAccessibilityLabel(intl: IntlShape, value: MeterAccessibility) {
  const fallback = accessibilityOptions.find(option => option.value === value)?.label ?? value;
  const accessibilityKey = accessibilityKeyByValue[value];

  return accessibilityKey ? intl.formatMessage(accessibilityMessage(accessibilityKey)) : fallback;
}
